import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Store, Search, ShieldCheck, ShieldAlert, ChevronRight } from 'lucide-react';

export default function EstablishmentsManager() {
    const [establishments, setEstablishments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchEstablishments();
    }, []);

    const fetchEstablishments = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('establishments')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setEstablishments(data || []);
        } catch (error) {
            console.error('Error fetching establishments:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleStatus = async (id, currentStatus) => {
        try {
            const { error } = await supabase
                .from('establishments')
                .update({ is_active: !currentStatus })
                .eq('id', id);

            if (error) throw error;

            // Update local state
            setEstablishments(establishments.map(est =>
                est.id === id ? { ...est, is_active: !currentStatus } : est
            ));
        } catch (error) {
            console.error('Error updating establishment status:', error);
            alert('Failed to update status');
        }
    };

    const filteredData = establishments.filter(est =>
        est.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        est.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <h1 style={{ fontSize: '1.75rem', fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Store color="var(--accent-primary)" /> Establishments
                    </h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Manage all registered restaurants and venues.</p>
                </div>

                <div style={{ position: 'relative', width: '300px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        className="form-input"
                        style={{ paddingLeft: '40px' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Restaurant Name</th>
                                <th>Location</th>
                                <th>Pin Code</th>
                                <th>Created Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '32px' }}>Loading...</td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>No establishments found.</td>
                                </tr>
                            ) : (
                                filteredData.map((est) => (
                                    <tr key={est.id}>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{est.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{est.email || 'No email provided'}</div>
                                        </td>
                                        <td>{est.address || '-'}</td>
                                        <td><code style={{ background: 'var(--bg-app)', padding: '4px 8px', borderRadius: '4px' }}>••••</code></td>
                                        <td>{new Date(est.created_at).toLocaleDateString()}</td>
                                        <td>
                                            {est.is_active ? (
                                                <span className="badge badge-success"><ShieldCheck size={14} style={{ marginRight: '4px' }} /> Active</span>
                                            ) : (
                                                <span className="badge badge-danger"><ShieldAlert size={14} style={{ marginRight: '4px' }} /> Inactive</span>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <Link
                                                    to={`/admin/establishments/${est.id}`}
                                                    className="btn btn-outline"
                                                    style={{ padding: '6px 12px', fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}
                                                >
                                                    Details <ChevronRight size={14} style={{ marginLeft: '4px' }} />
                                                </Link>
                                                <button
                                                    onClick={() => toggleStatus(est.id, est.is_active)}
                                                    className={`btn ${est.is_active ? 'btn-danger' : 'btn-primary'}`}
                                                    style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                                                >
                                                    {est.is_active ? 'Deactivate' : 'Activate'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
